/**
 * Keeps the screen on while something long is running (a photo upload).
 *
 * A phone that dims and locks mid-upload backgrounds the page, and a
 * backgrounded page -- above all inside the Facebook or Instagram app -- loses
 * its network. A screen wake lock removes the most common way that happens
 * without the customer doing anything.
 *
 * Best effort by design: the API is missing in older browsers and many in-app
 * WebViews, the request can be refused (battery saver, permissions policy), and
 * the browser drops the lock by itself whenever the page is hidden. None of
 * that may ever reach the upload, so every failure is swallowed, and the lock
 * is asked for again each time the page comes back into view.
 */

interface WakeLockSentinelLike {
  release(): Promise<void>;
  addEventListener?: (type: "release", listener: () => void) => void;
}

interface WakeLockEnv {
  nav?: { wakeLock?: { request(type: "screen"): Promise<WakeLockSentinelLike> } } | null;
  doc?: Pick<Document, "visibilityState" | "addEventListener" | "removeEventListener"> | null;
}

export interface WakeLockKeeper {
  start(): void;
  stop(): void;
}

export function createWakeLockKeeper(env: WakeLockEnv = {}): WakeLockKeeper {
  const nav =
    env.nav !== undefined
      ? env.nav
      : typeof navigator === "undefined"
        ? null
        : (navigator as unknown as WakeLockEnv["nav"]);
  const doc =
    env.doc !== undefined ? env.doc : typeof document === "undefined" ? null : document;

  let wanted = false;
  let requesting = false;
  let sentinel: WakeLockSentinelLike | null = null;

  const releaseQuietly = (s: WakeLockSentinelLike) => {
    try {
      s.release().catch(() => {});
    } catch {
      // Already released, or a browser that throws synchronously. Either way gone.
    }
  };

  const acquire = async () => {
    if (!wanted || sentinel || requesting) return;
    if (!nav?.wakeLock?.request) return;
    // Requesting from a hidden page is always refused; wait for visibility.
    if (doc && doc.visibilityState !== "visible") return;
    requesting = true;
    try {
      const s = await nav.wakeLock.request("screen");
      if (!wanted) {
        releaseQuietly(s);
        return;
      }
      sentinel = s;
      // The browser releases it on its own when the page is hidden.
      s.addEventListener?.("release", () => {
        if (sentinel === s) sentinel = null;
      });
    } catch {
      // Refused or unsupported. The upload carries on without it.
    } finally {
      requesting = false;
    }
  };

  const onVisibility = () => {
    if (doc?.visibilityState === "visible") void acquire();
  };

  return {
    start() {
      if (wanted) return;
      wanted = true;
      doc?.addEventListener("visibilitychange", onVisibility);
      void acquire();
    },
    stop() {
      wanted = false;
      doc?.removeEventListener("visibilitychange", onVisibility);
      const s = sentinel;
      sentinel = null;
      if (s) releaseQuietly(s);
    },
  };
}
